import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  CardMedia,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Paper,
  Button,
  Divider,
  Collapse,
  Link as MuiLink,
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon } from '@mui/icons-material';
import PublicNavBar from './PublicNavBar';
import {
  fetchPublicShareMeta,
  fetchPublicShareDetection,
  buildShareImageUrl,
} from '../services/publicShareService';

const capitalize = (s) => (s && typeof s === 'string') ? s.charAt(0).toUpperCase() + s.slice(1) : s;

const formatDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
};

const formatConfidence = (c) => (typeof c === 'number') ? `${(c * 100).toFixed(2)}%` : 'N/A';

const SharedDetectionPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [detection, setDetection] = useState(null);
  const [unavailable, setUnavailable] = useState(null); // { reason }
  const [error, setError] = useState(null);
  const [llmExpanded, setLlmExpanded] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setUnavailable(null);
    setMeta(null);
    setDetection(null);
    try {
      const metaData = await fetchPublicShareMeta(token);
      if (metaData && metaData.available === false) {
        setUnavailable({ reason: metaData.reason || 'unavailable' });
        return;
      }
      setMeta(metaData);
      const detectionData = await fetchPublicShareDetection(token);
      setDetection(detectionData);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 410 || status === 404) {
        const reason = err?.response?.data?.reason || (status === 404 ? 'not_found' : 'unavailable');
        setUnavailable({ reason });
      } else {
        console.error('Failed to load shared detection:', err);
        setError('Could not load this shared detection. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const renderUnavailable = () => (
    <Container maxWidth="sm" sx={{ mt: 12, mb: 8 }}>
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 6 }}>
          <Typography variant="h5" gutterBottom>
            This share link is no longer available
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {(() => {
              switch (unavailable?.reason) {
                case 'expired':
                  return 'The link expired.';
                case 'revoked':
                  return 'The owner revoked this link.';
                case 'exhausted':
                  return 'The view limit on this link has been reached.';
                case 'not_found':
                  return 'We could not find this share.';
                default:
                  return 'The link may have expired, been revoked, or reached its view limit.';
              }
            })()}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')}>
            Visit EyeSense.ai
          </Button>
        </CardContent>
      </Card>
    </Container>
  );

  const renderError = () => (
    <Container maxWidth="sm" sx={{ mt: 12, mb: 8 }}>
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={load}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    </Container>
  );

  const renderLoading = () => (
    <Container maxWidth="sm" sx={{ mt: 16, display: 'flex', justifyContent: 'center' }}>
      <CircularProgress />
    </Container>
  );

  const renderShare = () => {
    const imageSrc =
      detection?.presignedAnnotatedImageUrl ||
      buildShareImageUrl(token, 'annotated');
    const fallbackImageSrc = buildShareImageUrl(token, 'full');
    const title = meta?.label
      ? meta.label
      : (detection?.animalType ? `${capitalize(detection.animalType)} detected` : 'Shared detection');

    return (
      <Container maxWidth="md" sx={{ mt: 12, mb: 6 }}>
        <Card sx={{ overflow: 'hidden' }}>
          <CardMedia
            component="img"
            image={imageSrc}
            alt={title}
            onError={(e) => {
              if (e?.target && e.target.src !== window.location.origin + fallbackImageSrc) {
                e.target.src = fallbackImageSrc;
              }
            }}
            sx={{ width: '100%', maxHeight: 600, objectFit: 'contain', bgcolor: '#000' }}
          />
          <CardContent>
            <Typography variant="h4" gutterBottom>
              {title}
            </Typography>

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Stack spacing={1}>
                {detection?.cameraName && (
                  <Typography variant="body2"><strong>Camera:</strong> {detection.cameraName}</Typography>
                )}
                {detection?.detectionTime && (
                  <Typography variant="body2"><strong>Time:</strong> {formatDateTime(detection.detectionTime)}</Typography>
                )}
                <Typography variant="body2">
                  <strong>Confidence:</strong> {formatConfidence(detection?.confidence)}
                </Typography>
                {detection?.pipelineName && (
                  <Typography variant="body2"><strong>Pipeline:</strong> {detection.pipelineName}</Typography>
                )}
                {detection?.llmModelName && (
                  <Typography variant="body2"><strong>LLM model:</strong> {detection.llmModelName}</Typography>
                )}
              </Stack>
            </Paper>

            {detection?.matchedZoneNames?.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Matched zones</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  {detection.matchedZoneNames.map((z) => (
                    <Chip key={z} label={z} size="small" />
                  ))}
                </Stack>
              </Box>
            )}

            {detection?.llmResponse && (
              <Box sx={{ mb: 2 }}>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setLlmExpanded((v) => !v)}
                >
                  <Typography variant="subtitle2">LLM analysis</Typography>
                  {llmExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                </Stack>
                <Collapse in={llmExpanded || (detection.llmResponse || '').length < 240} unmountOnExit>
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, mt: 1, whiteSpace: 'pre-wrap', maxHeight: 320, overflowY: 'auto' }}
                  >
                    {detection.llmResponse}
                  </Paper>
                </Collapse>
              </Box>
            )}

            <Divider sx={{ my: 2 }} />

            <Typography variant="caption" color="text.secondary">
              Shared from{' '}
              <MuiLink href="/" underline="hover">EyeSense.ai</MuiLink>
              {' — link expires '}
              {meta?.expiresAt ? formatDateTime(meta.expiresAt) : 'never'}
              {'.'}
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <PublicNavBar />
      {loading && renderLoading()}
      {!loading && unavailable && renderUnavailable()}
      {!loading && !unavailable && error && renderError()}
      {!loading && !unavailable && !error && detection && renderShare()}
    </Box>
  );
};

export default SharedDetectionPage;
